import React, { useEffect, useRef, useState } from "react";
import {
  Box, Checkbox, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Paper,
  RadioGroup, TextField, Typography,
} from "@mui/material";
import { api } from "./api";
import WhatYouNeed from "./WhatYouNeed";
import LocationsSection from "./LocationsSection";
import {
  AddressFields, ErrorBanner, Field, FieldLabel, OptionTile, OutlineButton, PreviewLock, SecretField, StateField, StepActions, StepCard, SubHeading,
  brandCheckboxSx, fieldId, secretProps,
} from "./formParts";
import { Icon, T } from "./signupDesign";

/** Save one section; returns true when the server found nothing to fix. */
function useSectionSave(section, token, onSaved) {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  // tokenOverride: step 1 saves with the token Start just returned, before
  // the new token has reached this component as a prop.
  const save = async (data, tokenOverride) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api("PUT", `/application/${section}`, { token: tokenOverride || token, body: data });
      const errs = res.errors || {};
      setErrors(errs);
      onSaved(res);
      if (Object.keys(errs).length) {
        setError("Please fix the highlighted fields.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return false;
      }
      return true;
    } catch (e) {
      setErrors(e.details || {});
      setError(e.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    } finally {
      setBusy(false);
    }
  };
  // Without a word on screen: the page saves the step this way when moving
  // to another one (the step list, Back).
  const saveQuietly = (data) => api("PUT", `/application/${section}`, { token, body: data }).then(onSaved);
  return { busy, setBusy, errors, setErrors, error, setError, save, saveQuietly };
}

/** Has this changed since the step opened? */
function useChanged(value) {
  const first = useRef(null);
  const now = JSON.stringify(value);
  if (first.current === null) first.current = now;
  return now !== first.current;
}

/**
 * While the step has changes, tell the page how to save them, so moving to
 * another step without Save & continue doesn't lose them.
 */
export function useDraft(registerDraft, changed, saveDraft) {
  useEffect(() => {
    if (!registerDraft) return undefined;
    registerDraft(changed ? saveDraft : null);
    return () => registerDraft(null);
  });
}

// 18px between rows, 16px between columns.
const GRID = { container: true, rowSpacing: 2.25, columnSpacing: 2 };

// A gray prefix attached to the left of an input ("https://").
const prefixSx = {
  alignSelf: "stretch", height: "auto", maxHeight: "none", m: 0, ml: "-14px", mr: "12px", px: "14px",
  background: T.fill, borderRight: `1px solid ${T.input}`, borderRadius: "11px 0 0 11px",
  "& .MuiTypography-root": { color: T.hint, fontSize: 15 },
};

/** A competitor rate, or "Unknown" for an owner who doesn't know it. */
function RateField({ label, name, value, onChange, helperText }) {
  const unknown = value === "Unknown";
  const id = fieldId(name);
  return (
    <Grid item xs={12}>
      <FieldLabel htmlFor={id} label={label} optional />
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          id={id}
          value={unknown ? "" : value || ""}
          placeholder={unknown ? "Unknown" : undefined}
          disabled={unknown}
          onChange={(e) => onChange(e.target.value)}
          helperText={helperText}
        />
        <FormControlLabel
          sx={{ whiteSpace: "nowrap", mt: 0.5, mr: 0 }}
          control={<Checkbox checked={unknown} onChange={(e) => onChange(e.target.checked ? "Unknown" : "")} sx={brandCheckboxSx} />}
          label="Unknown"
        />
      </Box>
    </Grid>
  );
}

// Start's field names → step 1's, so its errors land on the right inputs.
const START_FIELDS = { restaurantName: "dbaName", firstName: "contactFirst", lastName: "contactLast", email: "email", phone: "phone" };

const sectionTitle = (title, first) => <Grid item xs={12}><SubHeading first={first} sx={{ mb: 0 }}>{title}</SubHeading></Grid>;

// ── 1. Business (what customers see) ────────────────────────────────────────

export function BusinessStep({ view, meta, token, preview, onStart, onSaved, onNext, staffStarting, registerDraft }) {
  const [f, setF] = useState(() => ({ ...view.application.business }));
  const { busy, setBusy, errors, setErrors, error, setError, save, saveQuietly } = useSectionSave("business", token, onSaved);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  // One phone number: the server keeps the customer-service line in step
  // with it, so the old value is never sent back.
  const body = () => {
    const { csPhone, ...rest } = f;
    return rest;
  };
  useDraft(registerDraft, useChanged(f), () => saveQuietly(body()));
  const next = async () => {
    if (!preview) {
      if (await save(body())) onNext();
      return;
    }
    // Not started yet: Start creates the application (and emails the link),
    // then the rest of this step is saved onto it.
    setBusy(true);
    setError(null);
    let started;
    try {
      started = await onStart({
        businessType: f.businessType,
        restaurantName: f.dbaName,
        firstName: f.contactFirst,
        lastName: f.contactLast,
        email: f.email,
        phone: f.phone,
      });
    } catch (e) {
      setErrors(Object.fromEntries(Object.entries(e.details || {}).map(([k, v]) => [START_FIELDS[k] || k, v])));
      setError(e.message);
      setBusy(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (await save(body(), started.token)) onNext();
  };
  const grocery = f.businessType === "GROCERY";
  return (
    <StepCard title={grocery ? "Your store" : "Your restaurant"} subtitle="The name, address and contact details your customers will see.">
      <ErrorBanner error={error} />
      <Grid {...GRID}>
        {sectionTitle("Business", true)}
        <Field select label="Type of business" name="businessType" value={f.businessType || "RESTAURANT"} onChange={set("businessType")} errors={errors} required sm={6}>
          {(meta.businessTypes || [{ value: "RESTAURANT", label: "Restaurant" }, { value: "GROCERY", label: "Grocery" }]).map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </Field>
        <Field label={grocery ? "Store name (DBA)" : "Restaurant name (DBA)"} name="dbaName" value={f.dbaName} onChange={set("dbaName")} errors={errors} required sm={6} inputProps={{ autoComplete: "organization" }} />

        {sectionTitle("Primary contact")}
        <Field label="First name" name="contactFirst" value={f.contactFirst} onChange={set("contactFirst")} errors={errors} required xs={12} sm={4} md={5} inputProps={{ autoComplete: "given-name" }} />
        <Field label="Middle" name="contactMiddle" value={f.contactMiddle} onChange={set("contactMiddle")} errors={errors} xs={6} sm={3} md={2} inputProps={{ autoComplete: "additional-name" }} />
        <Field label="Last name" name="contactLast" value={f.contactLast} onChange={set("contactLast")} errors={errors} required xs={6} sm={5} md={5} inputProps={{ autoComplete: "family-name" }} />
        <Field label="Email" name="email" type="email" value={f.email} onChange={set("email")} errors={errors} required sm={6} inputProps={{ autoComplete: "email" }} helperText={preview ? (staffStarting ? "The link to review and sign goes here when you send it." : "We'll also email your application link here.") : undefined} />
        <Field label="Phone number" name="phone" type="tel" value={f.phone} onChange={set("phone")} errors={errors} required sm={6} inputProps={{ autoComplete: "tel" }} />

        {sectionTitle(grocery ? "Store address" : "Restaurant address")}
        <AddressFields value={f} onChange={(a) => setF((x) => ({ ...x, ...a }))} errors={errors} states={meta.states} />
        <Field label="Website" name="website" value={f.website} onChange={set("website")} errors={errors} disabled={f.noWebsite} placeholder="myrestaurant.com" inputProps={{ autoComplete: "url" }} InputProps={{ startAdornment: <InputAdornment position="start" sx={prefixSx}>https://</InputAdornment> }} />
        <Grid item xs={12} sx={{ mt: -1 }}>
          <FormControlLabel control={<Checkbox checked={!!f.noWebsite} onChange={(e) => set("noWebsite")(e.target.checked)} sx={brandCheckboxSx} />} label="We don't have a website" />
        </Grid>

        <Grid item xs={12}>
          <LocationsSection
            locations={f.locations}
            onChange={(locations) => setF((x) => ({ ...x, locations }))}
            errors={errors}
            states={meta.states}
            ownerRestaurants={(view.staff && view.staff.ownerRestaurants) || []}
            grocery={grocery}
            readOnly={!preview && !!view.status && view.status !== "DRAFT"}
          />
        </Grid>

        {sectionTitle("Your current setup")}
        <Field select label="Do you currently have online ordering on your website?" name="hasOnlineOrdering" value={f.hasOnlineOrdering} onChange={set("hasOnlineOrdering")} errors={errors} sm={6}>
          <MenuItem value="NO">No</MenuItem>
          <MenuItem value="YES">Yes</MenuItem>
        </Field>
        <RateField label="What is your current DoorDash Rate?" name="doordashRate" value={f.doordashRate} onChange={set("doordashRate")} helperText="You can find this in your dashboard under Settings → Pricing Plans. If you need help, we can help you." />
        <RateField label="What is your current UberEats Rate?" name="ubereatsRate" value={f.ubereatsRate} onChange={set("ubereatsRate")} helperText="You can find this in your dashboard under Payments → Payouts. If you need help, we can help you." />
        <RateField label="What is your current Grubhub Rate?" name="grubhubRate" value={f.grubhubRate} onChange={set("grubhubRate")} helperText="You can find this under Financials → Download the latest Statement. If you need help, we can help you." />
        <Grid item xs={12}>
          <OptionTile checked={!!f.interestedInPos} onChange={(e) => set("interestedInPos")(e.target.checked)} label="Are you interested in a free POS and No Fee Credit Card Processing?" />
        </Grid>
      </Grid>
      <StepActions onNext={next} busy={busy} nextLabel={preview ? (staffStarting ? "Create the application" : "Start my application") : undefined} />
    </StepCard>
  );
}

// ── 2. Legal entity ─────────────────────────────────────────────────────────

export function LegalStep({ view, meta, token, preview, onSaved, onNext, onBack, registerDraft }) {
  const saved = view.application.legal;
  const [f, setF] = useState(() => ({ ...saved, ein: "" }));
  const { busy, errors, error, save, saveQuietly } = useSectionSave("legal", token, onSaved);
  useDraft(registerDraft, useChanged(f), () => saveQuietly(f));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const soleProp = Number(f.entityType) === 0;
  const next = async () => {
    if (preview) return onNext();
    if (await save(f)) onNext();
  };
  return (
    <StepCard title="Legal entity" subtitle="Exactly as registered with the IRS and your state. The payment processors check these against public records.">
      {/* Right after the first part, not on the page people land on. */}
      {!preview && <WhatYouNeed />}
      <ErrorBanner error={error} />
      <PreviewLock on={preview}>
      <Grid {...GRID}>
        {sectionTitle("Registered business", true)}
        <Field label="Business legal name" name="legalName" value={f.legalName} onChange={set("legalName")} errors={errors} required placeholder="e.g. Sage's Fusion Kitchen LLC" inputProps={{ autoComplete: "organization" }} />
        <Field select label="Business entity type" name="entityType" value={f.entityType} onChange={set("entityType")} errors={errors} required sm={6}>
          {meta.entityTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </Field>
        <SecretField
          label={soleProp ? "EIN or SSN" : "Federal Tax ID (EIN)"}
          name="ein" value={f.ein} onChange={set("ein")} errors={errors} required sm={6}
          inputProps={{ inputMode: "numeric" }}
          helperText="9 digits, no dashes needed."
          savedLast4={saved.einLast4} mask="•••••"
        />
        <Field label="Business start date" name="startDate" type="date" value={f.startDate} onChange={set("startDate")} errors={errors} required sm={6} />
        <Field label="Business phone" name="phone" type="tel" value={f.phone} onChange={set("phone")} errors={errors} required sm={6} inputProps={{ autoComplete: "tel" }} />

        {sectionTitle("Legal business address")}
        <Grid item xs={12}>
          <OptionTile
            checked={!!f.sameAddress}
            onChange={(e) => set("sameAddress")(e.target.checked)}
            label="Same as the restaurant address"
            description="Uncheck this if the business is registered somewhere else, such as an owner's home or your accountant's office."
          />
        </Grid>
        {!f.sameAddress && (
          <AddressFields value={f} onChange={(a) => setF((x) => ({ ...x, ...a }))} errors={errors} states={meta.states} label="Legal business address" />
        )}
      </Grid>
      </PreviewLock>
      <StepActions onBack={onBack} onNext={next} busy={busy} />
    </StepCard>
  );
}

// ── 3. Card processing ──────────────────────────────────────────────────────

// The transaction mix, accepted card types and sales estimates are a
// platform standard a super admin sets (admin Signup Standards), so they
// aren't asked here.

export function ProcessingStep({ view, meta, token, preview, onSaved, onNext, onBack, registerDraft }) {
  const [f, setF] = useState(() => ({ ...view.application.processing }));
  const { busy, errors, error, save, saveQuietly } = useSectionSave("processing", token, onSaved);
  useDraft(registerDraft, useChanged(f), () => saveQuietly(f));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const next = async () => {
    if (preview) return onNext();
    if (await save(f)) onNext();
  };
  return (
    <StepCard title="Card processing" subtitle="Two quick questions so the processors can set up your account.">
      <ErrorBanner error={error} />
      <PreviewLock on={preview}>
      <Grid {...GRID}>
        <Grid item xs={12}>
          <FieldLabel id="rmf-processedBefore-label" label="Have you processed cards before?" />
          <RadioGroup aria-labelledby="rmf-processedBefore-label" value={f.processedBefore || ""} onChange={(e) => set("processedBefore")(e.target.value)}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, minmax(0, 220px))" }, gap: 1.5 }}>
              <OptionTile radio value="YES" checked={f.processedBefore === "YES"} label="Yes" error={!!errors.processedBefore} sx={{ py: 1.5 }} />
              <OptionTile radio value="NO" checked={f.processedBefore === "NO"} label="No" error={!!errors.processedBefore} sx={{ py: 1.5 }} />
            </Box>
          </RadioGroup>
          {errors.processedBefore && <Typography sx={{ color: T.error, fontSize: 13, mt: 0.75 }}>{errors.processedBefore}</Typography>}
        </Grid>
        <Field label="What are you selling?" name="productDescription" value={f.productDescription} onChange={set("productDescription")} errors={errors} required multiline minRows={2} />
      </Grid>
      </PreviewLock>
      <StepActions onBack={onBack} onNext={next} busy={busy} />
    </StepCard>
  );
}

// ── 4. Owners ───────────────────────────────────────────────────────────────

const blankOwner = () => ({
  title: "", first: "", middle: "", last: "", dob: "", ssn: "", ownership: "", email: "", phone: "",
  address1: "", address2: "", city: "", state: "", zip: "", dlNumber: "", dlState: "", isSigner: false, politicallyExposed: false,
});

// Secrets arrive masked; the form starts them blank ("leave blank to keep").
const ownerForm = (o) => ({ ...o, ssn: "", dob: "", dlNumber: "" });

export function OwnersStep({ view, meta, token, preview, onSaved, onNext, onBack, registerDraft }) {
  const saved = view.application.owners || [];
  const [owners, setOwners] = useState(() => (saved.length ? saved.map(ownerForm) : [{ ...blankOwner(), isSigner: true }]));
  const { busy, errors, error, save, saveQuietly } = useSectionSave("owners", token, onSaved);
  useDraft(registerDraft, useChanged(owners), () => saveQuietly({ owners }));
  const setOwner = (i, k) => (v) => setOwners((arr) => arr.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const setSigner = (i) => setOwners((arr) => arr.map((o, idx) => ({ ...o, isSigner: idx === i })));
  const total = owners.reduce((s, o) => s + (Number(o.ownership) || 0), 0);
  const next = async () => {
    if (preview) return onNext();
    if (await save({ owners })) onNext();
  };
  const savedOf = (o) => saved.find((s) => s._key && s._key === o._key) || {};
  return (
    <StepCard
      title="Owners"
      subtitle="List everyone who owns 25% or more of the business, plus the person who will sign the agreement. The processors verify each person's identity; this is required by U.S. law for every merchant account."
    >
      <ErrorBanner error={error} />
      {errors.owners && <ErrorBanner error={errors.owners} />}
      <PreviewLock on={preview}>
      {owners.map((o, i) => {
        const prefix = `owners.${i}.`;
        const s = savedOf(o);
        return (
          <Paper
            key={o._key || `new-${i}`}
            elevation={0}
            sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: "18px", border: `1px solid ${o.isSigner ? "#E6B8AD" : T.cardBorder}`, background: T.card }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: 18, color: T.text, minWidth: 0, overflowWrap: "anywhere" }}>
                Owner {i + 1}{o.first ? `: ${o.first} ${o.last}` : ""}
              </Typography>
              {owners.length > 1 && (
                <IconButton aria-label={`Remove owner ${i + 1}`} onClick={() => setOwners((arr) => arr.filter((_, idx) => idx !== i))} sx={{ width: 44, height: 44, color: T.muted }}>
                  <Icon.Trash size={20} />
                </IconButton>
              )}
            </Box>
            <Grid {...GRID}>
              <Field label="First name" name={`${prefix}first`} value={o.first} onChange={setOwner(i, "first")} errors={errors} required xs={12} sm={4} md={5} inputProps={{ autoComplete: "given-name" }} />
              <Field label="Middle" name={`${prefix}middle`} value={o.middle} onChange={setOwner(i, "middle")} errors={errors} xs={6} sm={3} md={2} inputProps={{ autoComplete: "additional-name" }} />
              <Field label="Last name" name={`${prefix}last`} value={o.last} onChange={setOwner(i, "last")} errors={errors} required xs={6} sm={5} md={5} inputProps={{ autoComplete: "family-name" }} />
              <Field label="Title" name={`${prefix}title`} value={o.title} onChange={setOwner(i, "title")} errors={errors} required sm={6} placeholder="Owner, Managing Member, President…" inputProps={{ autoComplete: "organization-title" }} />
              <Field label="Ownership" name={`${prefix}ownership`} type="number" value={o.ownership} onChange={setOwner(i, "ownership")} errors={errors} required sm={6} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ min: 0, max: 100 }} />
              <Field label="Date of birth" name={`${prefix}dob`} value={o.dob} onChange={setOwner(i, "dob")} errors={errors} required sm={6} placeholder="MM/DD/YYYY" inputProps={{ inputMode: "numeric", autoComplete: "off" }} helperText={s.dobSet ? "Saved. Leave blank to keep it." : undefined} />
              <SecretField label="Social Security number" name={`${prefix}ssn`} value={o.ssn} onChange={setOwner(i, "ssn")} errors={errors} required sm={6} inputProps={{ inputMode: "numeric" }} savedLast4={s.ssnLast4} mask="•••-••-" />
              <Field label="Email" name={`${prefix}email`} type="email" value={o.email} onChange={setOwner(i, "email")} errors={errors} required sm={6} inputProps={{ autoComplete: "email" }} />
              <Field label="Mobile phone" name={`${prefix}phone`} type="tel" value={o.phone} onChange={setOwner(i, "phone")} errors={errors} required sm={6} inputProps={{ autoComplete: "tel" }} />
              {sectionTitle("Home address")}
              <AddressFields value={o} onChange={(a) => setOwners((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...a } : x)))} errors={errors} prefix={prefix} states={meta.states} label="Home street address" />
              {sectionTitle("Photo ID")}
              <Field label="Driver's license or state ID number" name={`${prefix}dlNumber`} value={o.dlNumber} onChange={setOwner(i, "dlNumber")} errors={errors} required sm={7} inputProps={{ autoComplete: "off" }} {...secretProps(s.dlNumberLast4, "••••")} />
              <StateField states={meta.states} label="Issuing state" name={`${prefix}dlState`} value={o.dlState} onChange={setOwner(i, "dlState")} errors={errors} required sm={5} />
              <Grid item xs={12} sm={6}>
                <OptionTile radio checked={!!o.isSigner} onChange={() => setSigner(i)} label="This person will sign the agreement" sx={{ height: "100%" }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OptionTile checked={!!o.politicallyExposed} onChange={(e) => setOwner(i, "politicallyExposed")(e.target.checked)} label="Holds, or has held, a senior political or government position" sx={{ height: "100%" }} />
              </Grid>
            </Grid>
          </Paper>
        );
      })}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: total > 100 ? T.error : T.muted }}>Ownership listed: {total}%</Typography>
        {owners.length < meta.maxOwners && (
          <OutlineButton startIcon={<Icon.Plus size={18} />} onClick={() => setOwners((arr) => [...arr, blankOwner()])}>
            Add another owner
          </OutlineButton>
        )}
      </Box>
      </PreviewLock>
      <StepActions onBack={onBack} onNext={next} busy={busy} />
    </StepCard>
  );
}

// ── 5. Payout bank ──────────────────────────────────────────────────────────

export function BankStep({ view, meta, token, preview, onSaved, onNext, onBack, registerDraft }) {
  const saved = view.application.bank;
  const [f, setF] = useState(() => ({
    ...saved,
    holderName: saved.holderName || view.application.legal.legalName || "",
    account: "",
    routingConfirm: saved.routing || "",
    accountConfirm: "",
  }));
  const { busy, errors, error, save, saveQuietly } = useSectionSave("bank", token, onSaved);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const bankBody = () => {
    const body = { ...f };
    if (!body.account) delete body.accountConfirm;
    return body;
  };
  useDraft(registerDraft, useChanged(f), () => saveQuietly(bankBody()));
  const next = async () => {
    if (preview) return onNext();
    if (await save(bankBody())) onNext();
  };
  return (
    <StepCard title="Where do we send your money?" subtitle="Your payouts are deposited here, and Schedule B authorizes RushMyFood to debit and credit this account.">
      <ErrorBanner error={error} />
      <PreviewLock on={preview}>
      <Grid {...GRID}>
        {sectionTitle("Bank account", true)}
        <Field label="Bank name" name="bankName" value={f.bankName} onChange={set("bankName")} errors={errors} required sm={6} />
        <Field select label="Account type" name="accountType" value={f.accountType} onChange={set("accountType")} errors={errors} required sm={6}>
          {meta.accountTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </Field>
        <Field label="Name on the account" name="holderName" value={f.holderName} onChange={set("holderName")} errors={errors} required />
        {sectionTitle("Routing and account numbers")}
        <SecretField label="Routing number" name="routing" value={f.routing} onChange={set("routing")} errors={errors} required sm={6} inputProps={{ inputMode: "numeric", maxLength: 9 }} />
        <SecretField label="Confirm routing number" name="routingConfirm" value={f.routingConfirm} onChange={set("routingConfirm")} errors={errors} required sm={6} inputProps={{ inputMode: "numeric", maxLength: 9 }} />
        <SecretField label="Account number" name="account" value={f.account} onChange={set("account")} errors={errors} required sm={6} inputProps={{ inputMode: "numeric" }} savedLast4={saved.accountLast4} mask="••••" />
        <SecretField label="Confirm account number" name="accountConfirm" value={f.accountConfirm} onChange={set("accountConfirm")} errors={errors} required={!saved.accountLast4 || !!f.account} sm={6} disabled={!!saved.accountLast4 && !f.account} inputProps={{ inputMode: "numeric" }} />
      </Grid>
      </PreviewLock>
      <StepActions onBack={onBack} onNext={next} busy={busy} />
    </StepCard>
  );
}
